// src/components/dashboards/composition/settingsCopy.ts
//
// The wording of the dashboard editor's settings (plan 2026-10-10 dashboard
// settings catalogue, A2; ADR-0378): every customer-facing label,
// description, option label and section summary the block, control,
// dashboard and filter panels show.
//
// THE RULE: a customer-facing settings string lives here once; forms import
// it. Two spellings of one setting is how two panels stop agreeing, so
// `settingsCopy.test.ts` refuses a form that writes one of these strings as
// a literal again. Values (enums, bounds, defaults) are not wording: those
// are generated from the backend (`COMPOSITION_SETTINGS`, A1).
//
// Grouped by surface, then by section, in the order the panels show them.
// A string with a number or a name in it is a function, so the sentence
// stays whole here instead of being glued together in a form.

/** "1 filter" / "3 filters": one count rule for every noun below. */
const count = (n: number, one: string, many: string) => (n === 1 ? `1 ${one}` : `${n} ${many}`);

/** Words two groups below share, so the module spells each once. */
const GROUP_BY = "Group by";
const DESCRIPTION = "Description";
const RETRY = "Try again";

export const SETTINGS_COPY = {
  /** Every setting that follows a level above (plan 2026-10-17-dashboard-
   *  text-style-complete, D1): the closed control shows the value in effect,
   *  the list marks the inherited value, and an override says what it
   *  overrides. */
  inheritance: {
    defaultSuffix: " (default)",
    /** `from` is "Default" (Verity's), "Account" or "Dashboard". */
    overrides: (from: string, value: string) =>
      from === "Default"
        ? `Overrides Verity's default (${value}).`
        : `Overrides the ${from.toLowerCase()} default (${value}).`,
    /** The tooltip of a value that follows the level above. */
    follows: (value: string) => `${value} (default)`,
  },
  /** Shared by several sections. */
  common: {
    chooseDatasetFirst: "Choose a dataset first.",
    addDimension: "Add dimension",
    addBreakdown: "Add breakdown",
    wholePage: "Whole page",
    /** A chart's X axis and a table's Rows: the same two shapes. */
    overTime: "Over time",
    byDimension: "By dimension",
    /** The button beside a failed load or write in a settings panel. */
    retry: RETRY,
  },

  /** What a closed section says on its row (`sectionSummaries.ts`). */
  summary: {
    none: "None",
    off: "Off",
    default: "Default",
    filters: (n: number) => count(n, "filter", "filters"),
    /** Interactions with a list: "1 block", "3 charts". */
    targets: (n: number, noun: string) => count(n, noun, `${noun}s`),
    /** Interactions while the canvas picker edits this source's targets. */
    choosing: (noun: string) => `Choosing ${noun}s`,
    sortedBy: (label: string, ascending: boolean) => `${label}${ascending ? ", lowest first" : ""}`,
    /** "Top 10" alone, "top 10" after a sort. */
    top: (n: number, first: boolean) => (first ? `Top ${n}` : `top ${n}`),
  },

  source: {
    label: "Source",
    placeholder: "Choose a source",
    loading: "Loading...",
    moreDataGroup: "More data",
    addSource: "Add data source",
  },

  dataset: {
    label: "Dataset",
    placeholder: "Choose a dataset",
    dbtDescription: "Defined in your dbt project",
    viewInGitHub: "View in GitHub",
    dbtBadge: "dbt",
  },

  metrics: {
    label: "Metrics",
    noun: "metrics",
    add: "Add metric",
    newCalculated: "New calculated metric",
  },

  dimensions: {
    label: "Dimensions",
  },

  /** A chart's breakdown (or a sankey's "To"). */
  breakdown: {
    label: "Break down by",
    description: "One series per value of this dimension.",
    flowLabel: "To",
    flowDescription: "Where each flow ends; the dimension above is where it starts.",
    oneMetricNote: "A breakdown splits one metric - keep one metric to add it.",
    /** A scatter or bubble on a dashboard: the breakdown colours the points
     *  and keeps every metric (plan 2026-10-16 seasonal heatmap, D3). */
    colourByLabel: "Colour by",
    colourByDescription: "One colour per value of this dimension; every point keeps its position and size.",
  },

  filters: {
    title: "Filters",
    /** The block editor's Filters tab before a dataset is chosen. */
    needsDataset: "Choose a source and dataset in Configure first.",
    /** A P&L before its statement is chosen. */
    needsStatement: "Choose a statement in Configure first.",
    /** The Filters tab's line under its heading, in the block's own noun. */
    tabDescription: (noun: string) => `Conditions for this ${noun}.`,
    /** The Filters tab's footnote. */
    blockOnly: "Filters apply to this block only.",
    saved: {
      label: "Saved filters",
      placeholder: "Add a saved filter",
      blockDescription: "Applied on top of the dashboard's and the page's filters.",
    },
    rows: {
      fieldAria: "Filter field",
      fieldPlaceholder: "Field",
      fieldsLoading: "Loading fields…",
      operatorAria: "Filter operator",
      valueAria: "Filter value",
      valuePlaceholder: "Value",
      listPlaceholder: "Comma separated",
      removeAria: "Remove filter",
      add: "Add filter",
      addCondition: "Add condition",
    },
    /** By the AbstractFilter value they label (`blockForms::FILTER_OPERATORS`). */
    operators: {
      "=": "is",
      "!=": "is not",
      IN: "is one of",
      "NOT IN": "is not one of",
      ">": "greater than",
      ">=": "greater than or equal to",
      "<": "less than",
      "<=": "less than or equal to",
    },
    /** The read-only list and the filter modal (plan 2026-10-11 filter modal
     *  with impact). */
    list: {
      editAria: (field: string) => `Edit filter on ${field}`,
      removeAria: (field: string) => `Remove filter on ${field}`,
      unavailable: "no longer available",
      metricBadge: "Metric condition",
      dimensionBadge: "Filter",
    },
    modal: {
      addTitle: "Add filter",
      editTitle: "Edit filter",
      source: "Source",
      dataset: "Dataset",
      field: "Field",
      fieldPlaceholder: "Choose a field",
      dimensionsGroup: "Dimensions",
      joinedGroup: "From joined datasets",
      metricsGroup: "Metrics",
      operator: "Condition",
      value: "Value",
      valuesPlaceholder: "Pick or type values",
      valuePlaceholder: "Pick or type a value",
      booleanPlaceholder: "Pick true or false",
      numberPlaceholder: "Number",
      lockedSource: "A block filter uses the block's own source and dataset.",
      noSources: "No data sources to filter on yet.",
      metricsPerBlock: "Metric conditions are set per block, because they depend on what the block groups by.",
      metricsNotOnKpi: "A single number has no rows to filter, so it takes no metric conditions.",
      metricsNotOnPivot: "A pivot table does not take metric conditions yet: its subtotals group the rows again.",
      metricsNotOnBreakdown: "Metric conditions are not supported yet on a chart over time split by a dimension.",
      metricsNotInReports: "Report blocks do not take metric conditions yet.",
      cancel: "Cancel",
      add: "Add filter",
      save: "Save filter",
      discardTitle: "Discard this filter?",
      discardBody: "Your changes to this filter are not saved.",
      keepEditing: "Keep editing",
      discard: "Discard",
      changedElsewhere: "This filter was changed elsewhere.",
      addAsNew: "Add as new",
    },
    impact: {
      title: "Impact",
      reaches: "Filters these blocks",
      notAffected: "Not affected",
      notAffectedReason: (field: string) => `their dataset has no ${field}`,
      thisBlock: "This block",
      measure: "Measured by",
      before: "Without this filter",
      after: "With this filter",
      rows: (before: string, after: string) => `Rows: ${before} → ${after}`,
      rowsCapped: "at least",
      values: "Values",
      valuesHint: "Click a value to add it.",
      noValues: "No values in this period.",
      morevalues: "Showing the top 50.",
      pickField: "Choose a field to see its impact.",
      loading: "Calculating…",
      error: "Couldn't calculate the impact.",
      period: (start: string, end: string) => `${start} – ${end}`,
    },
  },

  kpi: {
    style: "Style",
    /** Which plan line from Targets the tile draws (Target / Budget / Guardrail). */
    planLine: {
      label: "Plan line",
      description: "The plan line from Targets that the tile draws.",
    },
    /** `kindLabel` is the plan line kind's label, lower-cased here. */
    targetHint: (kindLabel: string) => `Needs a ${kindLabel.toLowerCase()} for this metric`,
    border: {
      title: "Border",
      defaultLabel: "Card (default)",
    },
    /** The KPI's one headline metric (plan 2026-10-16 the secondary metric
     *  reads both ways, D4): a single dropdown, no count. */
    metric: {
      label: "Metric",
    },
    /** A scorecard's secondary metric (plans 2026-10-16 a scorecard carries
     *  an optional secondary line, D5, and the secondary metric reads both
     *  ways, D2). The stored key stays `secondary_line`; only the copy says
     *  "Secondary metric". `label` / `card` are metric labels. */
    secondaryLine: {
      title: "Secondary metric",
      none: "None",
      /** The collapsed section's summary per Show as. */
      summary: {
        value: (label: string) => label,
        share: (label: string) => `Card as % of ${label}`,
        share_of_card: (label: string) => `${label} as % of card`,
      },
      metric: "Metric",
      showAs: "Show as",
      /** The Show as options. */
      modes: {
        value: "Value",
        share: "% of metric",
        share_of_card: "% of card",
      },
      /** The help line under Show as: what is divided by what. */
      help: {
        value: (label: string) => `The value of ${label} for the same period.`,
        share: (label: string, card: string) => `${card} divided by ${label}, as a %.`,
        share_of_card: (label: string, card: string) => `${label} divided by ${card}, as a %.`,
      },
      shareNeedsSameUnit: (headline: string) =>
        `% of metric and % of card need a metric in the same unit as ${headline}.`,
      chooseHeadlineFirst: "Choose a metric for the card first.",
      textBefore: "Text before",
      textAfter: "Text after",
      /** The text inputs' placeholders per Show as. */
      placeholders: {
        value: { before: "e.g. Discounts", after: "e.g. incl. VAT" },
        share: { before: "e.g. as % of Discounts", after: "e.g. of discounts" },
        share_of_card: { before: "e.g. Discounts as % of revenue", after: "e.g. of revenue" },
      },
      /** The managed margin tiles: their extra metrics feed another feature. */
      blocked: "This card already reads extra metrics",
    },
  },

  chart: {
    xAxisTitle: "X axis",
    /** A sankey's first dimension. */
    fromLabel: "From",
    undrawnDimensions:
      "A chart over time draws one series per value of its breakdown. These dimensions are not drawn - remove them.",
    style: {
      title: "Style",
      field: "Chart style",
      loading: "Loading chart styles…",
      current: "Current style: ",
      retryAria: "Try again loading chart styles",
      retry: RETRY,
    },
  },

  sortLimit: {
    title: "Sort & limit",
    sortBy: "Sort by",
    defaultOrder: "Default order",
    changeIn: (label: string) => `Change in ${label}`,
    direction: {
      aria: "Sort direction",
      desc: "Highest first",
      asc: "Lowest first",
    },
    showTop: "Show top",
    showTopPlaceholder: (max: number) => `Up to ${max}`,
    /** A stored cap above the kind's (a copy, seed or MCP write skips the
     *  forms): shown as stored, never clamped until the author edits it. */
    showTopOutOfRange: (value: number, max: number) =>
      value < 1
        ? `Stored as ${value}; enter a number from 1 to ${max}.`
        : `Stored as ${value}, more than the ${max} this can show. Up to ${max} show until you change it.`,
    capNote: (max: number, rowsNoun: string) => `Leave empty to show up to ${max} ${rowsNoun}.`,
    /** A report table shows the rows that fit its cell. */
    capNoteFitsBlock: (max: number) => `Leave empty to show the rows that fit the block, up to ${max}.`,
    /** A dashboard table sorted by change: its SQL limit picks by value. */
    changeSortNote: "Applies while the dashboard compares periods. Rows are picked by value, so Show top is off.",
  },

  table: {
    rows: {
      title: "Rows",
      removeBreakdownNote: "Remove the breakdown to add a second dimension.",
    },
    metricsHint: "Listed in the same order as the table columns.",
    /** Its label is the chart's (`breakdown.label`). */
    breakdown: {
      description: "Rows group by this dimension and expand to show it.",
      oneDimensionNote: "Keep exactly one dimension to break down its rows.",
      versusColumn: "A second dimension is a second column; a breakdown is an expandable group.",
      veryWide: "With own comparison columns, more than three metrics makes a very wide table.",
    },
    /** How a table shows the header's comparison; "Comparison" alone is the
     *  viewer's chart switch, so the setting says what it chooses. */
    comparison: {
      title: "Comparison display",
      off: "Off",
      inline: "In the cell",
      columns: "Own columns",
      offDescription: "No change is shown, even when a comparison period is picked.",
      inlineDescription: "The change sits next to each value; the previous value appears on hover.",
      columnsDescription: "Every metric gets a Value, vs and Δ column.",
    },
    columnPicker: {
      label: "Let viewers choose columns",
      description: "Adds a Columns button to this table. Each viewer's choice is remembered in their own browser.",
    },
    shownByDefault: {
      label: "Shown by default",
      placeholderAll: "All metrics",
      placeholderNoMetrics: "Choose metrics first",
      description: "The other metrics stay available through the Columns button.",
    },
    /** The trend column (plan 2026-10-16-a-table-row-carries-a-sparkline, D5). */
    trend: {
      label: "Show a trend column",
      description: "A small line per row over the last completed periods.",
      noTimeAxis: "This dataset has no date to draw a trend over.",
      metric: "Metric",
      grain: "Period",
      grains: { weekly: "Week", monthly: "Month", quarterly: "Quarter" },
      periods: "Periods",
      periodsDescription: (min: number, max: number) => `How many completed periods the line covers (${min} to ${max}).`,
      /** A P&L's own section for it (S12): a statement has no comparison
       *  section to sit in. */
      sectionTitle: "Trend",
    },
    /** The bar column (plan 2026-10-17-a-table-shows-one-metric-as-a-bar-column, D3). */
    bar: {
      label: "Show a bar column",
      description: "One metric drawn as a bar per row, beside its numbers.",
      metric: "Metric",
    },
    /** Also the Format tab's section (`format`). */
    columnFormats: {
      title: "Column formats",
      description: "One unit and decimal count per column; Auto follows the column's values.",
      noMetrics: "Choose metrics first.",
      unitAria: (label: string) => `Unit for ${label}`,
      decimalsAria: (label: string) => `Decimals for ${label}`,
      /** The table header gear on a dashboard tile (`UnitDecimalsField`). */
      unit: "Unit",
      decimals: "Decimals",
      reset: "Reset",
      gearAria: (label: string) => `Format ${label}`,
      /** A stored unit or decimal count no control offers: shown as stored,
       *  never rewritten until the author picks another. */
      outOfRange: (stored: string) =>
        `Stored as ${stored}, which this control does not offer. Pick an option to change it.`,
    },
    statementTitle: "Statement",
    layoutTitle: "Layout",
  },

  /** Which dashboards the header period applies to (plan 2026-10-11
   *  dashboard period scope, A3). */
  periodScope: {
    overview: "Applies to all dashboards that have no period of their own.",
    inheriting: "Same period as your other dashboards.",
    own: "Only this dashboard.",
    followAll: "Use the period of all dashboards",
    /** A dashboard with a stored default period (ADR-0496 D3). */
    defaulted: "This dashboard's default period.",
    followDefault: "Use this dashboard's default period",
    /** The stored default no longer validates (plan 2026-10-26-a-stored-
     *  default-period-that-no-longer-validates-falls-back-silently, D2/D4):
     *  every viewer reads it; the repair depends on the mode. */
    defaultInvalid: (period: string) =>
      `This dashboard's default period no longer exists. Showing ${period} instead.`,
    removeDefault: "Remove the default",
    /** Follows the "Remove the default" link on a report. */
    orInReportSettings: ", or choose another under Settings → Report.",
    /** View mode: a plain dashboard can only remove its default (OQ3). */
    editToRepair: {
      dashboard: "Edit this dashboard to remove it.",
      report: "Edit this report to remove or replace it.",
    },
    defaultRemoved: "Default removed.",
    publishToFix: "Publish to fix it for readers.",
  },

  /** The other shifted period on KPI cards, in the dashboard period picker
   *  (plan 2026-10-11 dashboard period scope, C1/C2). */
  otherPeriod: {
    /** A fixed heading: it does not change meaning with the mode above it. */
    heading: "On KPI cards, also show",
    /** The other period by name — the mode the viewer did NOT pick. */
    names: { previous_period: "Previous period", previous_year: "Last year" },
    /** The checkbox: the concrete period with its dates. */
    option: (name: string, dates: string) => `${name} · ${dates}`,
    /** The tooltip on the button's "+ last year" / "+ previous period". */
    tooltip: (name: string, dates: string) => `${name}: ${dates}`,
    /** Why the option is off: the other window IS the primary one. */
    sameAsPrimary: (primary: string, period: string) => `Same as the ${primary} for ${period}`,
    /** The primary mode as the reason names it. */
    primaryNames: { previous_period: "previous period", previous_year: "previous year" },
  },

  pivot: {
    columns: {
      label: "Columns",
      periods: "Periods",
      dimension: "A dimension",
    },
    columnDimension: {
      label: "Column dimension",
      placeholder: "Choose a dimension",
    },
    rows: {
      label: "Rows",
      description: "Drag to set the order, outer level first. Each level below the first adds a subtotal per group.",
      maxLevels: (max: number) => `A table has up to ${max} row levels.`,
    },
    orderRowsBy: {
      label: "Order rows by",
      /** By the stored `row_order.by`. */
      options: {
        total: "Largest total first",
        label: "Label (A to Z)",
        sort_key: "A sort column",
      },
    },
    sortColumn: {
      label: "Sort column",
      description: "Orders the rows; not shown in the table.",
    },
    /** By the stored `columns.order` (plan 2026-10-16 seasonal heatmap, D2). */
    columnOrder: {
      label: "Column order",
      options: {
        total: "By total",
        label: "By label",
      },
    },
    /** The seasonal heatmap preset (plan 2026-10-16 seasonal heatmap, D5). */
    seasonalHeatmap: {
      label: "Seasonal heatmap",
      description: "Rows and columns in label order, no totals, cells shaded from low to high and negative values highlighted.",
      needsShape: "Pick two dimensions (rows first, then columns) and one metric.",
      needsDimensionColumns: "Set Columns to a dimension first.",
      rulesFailed: (detail: string) => `The heatmap's colour rules were not saved: ${detail}`,
    },
    totals: {
      title: "Totals",
      subtotals: "Subtotal per group",
      totalColumn: "Total column",
      totalRow: "Total row",
    },
    /** A statement tile: one sentence with the page name in bold. */
    statementNote: {
      before: "This table follows a financial statement. Edit its lines on the ",
      page: "Financial Statements",
      after: " page.",
    },
    /** The pivot's grain. "Period" is the date range (header, date range
     *  control); a grain is "Group by". */
    period: {
      label: GROUP_BY,
      follow: "Automatic",
      followGrain: (grain: string) => `Automatic · ${grain}`,
      notAvailable: (label: string) => `${label} (not available)`,
      unavailableDescription: "This data can't be grouped this way, so the table uses the nearest grouping it has.",
      viewerChoice: {
        label: "Let viewers choose the grouping",
        description: "Each viewer's choice is remembered in their own browser.",
      },
    },
  },

  /** The step waterfall's own fields (plan 2026-10-16-a-waterfall-with-
   *  subtotals, D6): its metrics ARE its steps. */
  stepWaterfall: {
    title: "Steps",
    steps: {
      label: "Steps",
      description: "Drag to set the order. A level is a total drawn from zero; a step adds to or subtracts from the running total.",
      readOnlyRefs: "These metrics were set up outside the editor, so only their order, roles and signs can be changed here.",
    },
    role: {
      label: (name: string) => `${name}: level or step`,
      level: "Level",
      step: "Step",
    },
    sign: {
      label: (name: string) => `${name}: add or subtract`,
      add: "Add",
      subtract: "Subtract",
    },
    upTo: {
      label: "Up to",
      description: "The level the chart shows up to when it opens.",
    },
  },

  /** The receipt's own fields (plan 2026-10-16-a-receipt-visualization,
   *  D2): its lines are the step list above; these are the rest. */
  receipt: {
    title: "Receipt",
    heading: {
      label: "Title",
      description: "A line at the top of the receipt, such as the shop's name.",
    },
    per: {
      label: "Per",
      description: "A count every line can be divided by, such as orders, sessions or customers. It is not a line itself.",
      none: "None",
    },
    perLabel: {
      label: "Word after “Per”",
      description: "Optional. Empty uses the metric's name, such as “Per sessions”.",
    },
    basis: {
      label: "Show",
      per: (noun: string) => `Per ${noun}`,
      total: "Total",
    },
    footer: {
      title: "Footer",
      description: "Up to two figures printed under the total. A footer metric is not a line.",
      none: "None",
      summary: (count: number) => (count === 1 ? "1 figure" : `${count} figures`),
      metric: "Footer metric",
      perLabel: (noun: string) => (noun ? `Divide per ${noun}` : "Divide by the Per metric"),
      perNeedsPer: "Pick a Per metric first.",
      perIsItself: "This is the Per metric itself.",
      prefix: "Text before",
      suffix: "Text after",
      add: "Add a footer figure",
      remove: (name: string) => `Remove ${name}`,
    },
    negativeLabel: {
      label: "Label when negative",
      description: "Replaces the total's name when it is below zero, such as “Shortfall”.",
    },
  },

  /** The block editor's Format tab (table and pivot). */
  format: {
    periodLabels: {
      label: "Period labels",
      description: "How the period columns are named.",
      defaultOption: (example: string) => `${example} (default)`,
    },
    /** A date part's values (plan 2026-10-16 date parts, S1): month,
     *  quarter and week values; a year always reads as its digits. */
    datePartLabels: {
      label: "Date labels",
      description: "How month, quarter and week values are named.",
      defaultOption: (example: string) => `${example} (default)`,
      saveFailed: "The date labels could not be saved.",
    },
    conditionalTitle: "Conditional formatting",
    conditionsNotAllowed: "Colour rules are not available on this dashboard.",
    /** A scorecard's number format (plan 2026-10-17 a KPI has a Format tab,
     *  D3; `KpiNumberFormatSection`): the block's override per result column,
     *  and the metric's own kind beside it. */
    kpi: {
      title: "Number format",
      description: "One unit and decimal count per number; Auto follows the metric's own format.",
      /** The kind line's affix words ("Number · 1 decimal · suffix ×"). */
      prefix: (affix: string) => `prefix ${affix}`,
      suffix: (affix: string) => `suffix ${affix}`,
      editMetric: "Edit metric",
      editMetricAria: (label: string) => `Edit metric ${label}`,
      /** Where an editable metric's own format is changed, while the editor
       *  has no way to open its form from here. */
      editHint: "Edit the metric itself under Data sources → View structure.",
    },
    /** A chart's colours (ADR-0476, plan 2026-10-18 chart colours, D5;
     *  `ChartColoursSection`): its palette, a colour per series for every
     *  chart on the dashboard or for this chart only, and the structural
     *  colours (waterfall steps, Other, Not set, a target). */
    colours: {
      title: "Colours",
      palette: "Palette",
      paletteDescription: "Series without a colour of their own take one from this palette, by value.",
      paletteOptions: { account: "Account", theme: "Theme colours", custom: "Custom" },
      noTheme: "Theme colours (this dashboard has none)",
      customLabel: "Custom palette",
      customSwatch: (n: number) => `Palette colour ${n}`,
      addColour: "Add colour",
      removeColour: (n: number) => `Remove palette colour ${n}`,
      moveColour: (n: number) => `Palette colour ${n}: drag, or use the arrow keys, to move it`,
      moved: (n: number, of: number) => `Moved to position ${n} of ${of}.`,
      coloursRepeat: (series: number, colours: number) =>
        `Colours repeat: this chart draws ${series} series and the palette has ${colours} colours.`,
      series: "Series",
      structural: "Structural",
      noSeries: "This chart draws no series yet.",
      seriesColour: (label: string) => `Colour of ${label}`,
      scopeLabel: (label: string) => `Where the colour of ${label} applies`,
      scopeDashboard: "All charts on this dashboard",
      scopeChart: "This chart only",
      pinned: (n: number) => `Set for the dashboard · used in ${n} ${n === 1 ? "chart" : "charts"}`,
      overridden: "Set for this chart",
      notStorable: "This value cannot get a colour of its own: it is too long or holds a control character.",
      notInData: "Not in the current data",
      lowContrast: "Low contrast against the tile background.",
      judged: "Coloured by its target. A colour for this chart only replaces the on/off-track colour.",
      /** A KPI's sparkline row (plan 2026-10-19 a KPI sparkline takes the
       *  series colour, D5): its own colour beats the trend colour, a
       *  dashboard colour applies only while the trend is neutral. */
      sparkline: "Sparkline",
      trendColour: "Trend colour",
      sparklinePinned: "Set for the dashboard · used when the trend is neutral",
      sparklineManaged: "A KPI's trend colour cannot be replaced on a platform-managed dashboard.",
      trendNotPinned: "A trend colour is never pinned: pick a colour for the sparkline first.",
      pinsNotAllowed: "Only someone who can edit this dashboard can set colours for all its charts.",
      reset: (label: string) => `Reset the colour of ${label}`,
      roles: {
        increase: "Increase",
        decrease: "Decrease",
        total: "Total",
        other: "Other",
        not_set: "Not set",
        target: "Target",
      } as Record<string, string>,
      pinCurrent: "Pin current colours",
      pinCurrentTooltip:
        "Sets what each series draws now as its colour on every chart of this dashboard. A colour that is one of theme colours 1 to 8 is pinned as that theme colour and follows the theme; any other colour is pinned as a fixed colour.",
      nothingToPin: "Every series already has the colour it draws now.",
      resetChart: "Reset colours of this chart",
      resetChartNothing: "This chart has no colours of its own.",
      saveFailed: "The colours could not be saved.",
    },
  },

  /** One section for a block and a control (`InteractionsSettings`). */
  interactions: {
    title: "Interactions",
    label: "Clicking this block filters the page",
  },

  /** Which blocks a click or a control steers (`AppliesTo`). `noun` is
   *  "chart" or "block"; its plural is the noun plus "s". */
  appliesTo: {
    label: "Applies to",
    selected: (plural: string) => `Selected ${plural}`,
    count: (reached: number, noun: string, carriers: number | null, period = false) =>
      `${reached === 1 ? `Applies to 1 ${noun}` : `Applies to ${reached} ${noun}s`} on this page` +
      `${carriers !== null ? ` (of ${carriers} ${period ? "with a period" : "that can take this field"})` : ""}.`,
    picking: (plural: string) => `Switch ${plural} on or off on the dashboard, then click Done above it.`,
    /** A date range control never reaches a block with a period of its own
     *  (ADR-0445 decision 5): the reason on its switch, and the count. */
    hasOwnPeriod: "Has its own period",
    ownPeriodCount: (n: number, noun: string) =>
      n === 1 ? `1 ${noun} has its own period and keeps it.` : `${n} ${noun}s have their own period and keep it.`,
  },

  /** A block's own period (`BlockPeriodSection`, ADR-0445): the editor's
   *  Date range section and the tile's "fixed" hint. */
  blockPeriod: {
    title: "Date range",
    follows: "Dashboard period",
    own: "Own period",
    periodLabel: "Period",
    customRow: "Custom dates",
    customRange: "From – to",
    compareLabel: "Compare with",
    compareModes: {
      none: "None",
      previous_period: "Previous period",
      previous_year: "Previous year",
      custom: "Custom dates",
    },
    withTarget: "Also compare against the target",
    followsHelp: "This block follows the dashboard's date range and its date controls.",
    ownHelp: (dates: string) =>
      `Always ${dates}. The dashboard's date range and its date controls do not change this block.`,
    summaryFollows: "Follows the dashboard",
    summaryOwn: (period: string, compare: string | null) => (compare ? `${period} · vs ${compare}` : period),
    /** The tile header's hint beside the period label. */
    fixedHint: "Fixed period — does not follow the dashboard date range",
  },

  /** An open control's panel (`ControlSettingsPanel`). */
  control: {
    fallbackTitle: "Control",
    nothingToConfigure: "This control has nothing to configure.",
    sections: {
      selection: "Selection",
      appearance: "Appearance",
      action: "Action",
      selectionRules: "Selection rules",
      availableValues: "Available values",
      period: "Period",
    },
    label: {
      label: "Label",
      description: "Shown above the control.",
      descriptionLeft: "Shown left of the values.",
      placeholder: "Optional label",
    },
    appliesToEmptyHint: "Pick a field first, then choose which charts it filters.",
    dateRangeNote:
      "Sets the period of the charts it applies to. The other charts keep the dashboard period.",
    dateRangeDefault: {
      label: "Starts on",
      description: "The period the charts show until a viewer picks another.",
      follow: "The dashboard period",
    },
    field: {
      label: "Field",
      placeholder: "Pick a field",
      noneOnPage: "No field on this page can be filtered",
      nothingFound: "No such field on this page",
      notOnPage: (key: string) => `${key} (not available on this page)`,
    },
    selection: {
      label: "Allow viewers to select",
      single: "One value",
      multi: "Multiple values",
    },
    defaults: {
      noField: "Pick a field to choose default values.",
      loading: "Loading values",
      unavailable: "No chart this control applies to carries this field.",
      description: "What the control shows before a viewer changes it. Leave empty to show everything.",
      singleLabel: "Default value",
      multiLabel: "Default values",
      placeholder: "Type a value…",
      loadFailed: "Values couldn't load. Type to search.",
    },
    labelPosition: {
      label: "Label position",
      top: "Above",
      left: "Left",
      leftDescription: "Controls stacked in a column line up. A very narrow control shows its label above.",
    },
    display: {
      aria: "Display",
      checkboxes: "Checkboxes",
      buttons: "Buttons",
    },
    perRow: {
      label: "Buttons per row",
      auto: "Auto",
      autoDescription: "Each button is as wide as its text.",
      fixedDescription: "Every button has the same width.",
    },
    moreMenu: {
      label: "Show other values in a menu",
      description: "Show top sets the number of buttons; a button after them lists the other values.",
    },
    search: "Show search",
    showMetric: {
      label: "Show metric values",
      description: "Display the chart's metric next to each value.",
    },
    selectAll: {
      label: 'Show "All" option',
      description: "Clears the selection to include every value.",
    },
    required: {
      label: "Always keep a value selected",
      description: "Viewers can change the selection, but not clear it.",
      needsDefault: "Choose a default value first.",
    },
    maxSelections: {
      label: "Maximum selections",
      placeholder: "No limit",
    },
    sort: {
      metric_desc: "Most relevant first",
      value_asc: "A–Z",
      value_desc: "Z–A",
    },
    limitDescription: (max: number) => `Maximum ${max} values.`,
    limitDescriptionMoreMenu: "How many buttons. The other values are in the menu after them.",
    relevantOnly: {
      label: "Only relevant values",
      description: "Only offer values with data under the page's other filters.",
    },
    button: {
      whenClicked: "When clicked",
      openLink: "Open a link",
      resetFilters: "Reset this page's filters and controls",
      link: {
        label: "Link",
        description: "A web address that starts with https://. It opens in a new tab.",
        placeholder: "https://",
      },
    },
  },

  /** The edit panel's Settings pane: publishing, and for one release the
   *  pointer to where the filter bar settings went (plan 2026-10-10
   *  settings catalogue, C2). */
  /** The Settings pane's Report section (plan 2026-10-22 step B3). */
  reportSettings: {
    brief: {
      title: "Brief",
      audience: "Written for",
      purpose: "Purpose",
      notChosen: "Not chosen",
      focus: "Focus",
      focusHint: "What the analysis should pay attention to.",
    },
    language: {
      label: "Language",
      hint: "The language the report's text is written in. New text uses it from the next edition on; text already written stays as it is.",
      options: { en: "English", nl: "Dutch" },
    },
    lens: {
      label: "Lens",
      hint: "How the analysis reads your data: its time frame, comparisons and checks.",
      none: "Standard for this audience",
    },
    period: {
      label: "Default period",
      hint: "The window an edition covers. Viewers can still pick another.",
      none: "None",
      stored: "Custom period",
      /** A stored default that no longer validates (plan 2026-10-26, D4). */
      invalid: "Saved default no longer exists",
      invalidHint: "Pick a period, or None to remove the default.",
      storedComparison: "Custom dates",
      noTargets: "No targets yet: KPIs show no target row.",
      comparison: "Compare with",
      comparisons: {
        none: "Nothing",
        previous_period: "Previous period",
        previous_year: "Same period last year",
      } as Record<string, string>,
    },
    page: {
      label: "Paper",
      portrait: "Portrait",
      landscape: "Landscape",
      hint: "Every section of the report prints on this paper.",
    },
  },
  dashboardSettings: {
    /** The Settings pane's sections (plan 2026-10-13, Arjen's design): a
     *  title and the line under it that says what the section holds. */
    sections: {
      report: "Report",
      reportSummary: "Brief, language, lens, period & paper",
      general: "General",
      generalSummary: "Name, description & icon",
      appearance: "Appearance",
      appearanceSummary: (count: number, followsAccount: boolean) =>
        `${count} theme colours · ${followsAccount ? "Account defaults" : "Custom"}`,
      layout: "Layout",
      layoutSummary: (free: boolean) => (free ? "Free" : "Grid"),
      publishing: "Publishing",
      publishingSummary: (manual: boolean, review: boolean) =>
        manual ? `Manual · ${review ? "Review required" : "No review"}` : "Live",
    },
    /** Name, description and icon (`DashboardIdentitySection`, C3). */
    identity: {
      name: {
        label: "Name",
        required: "Enter a name for this dashboard.",
        tooLong: (max: number) => `Use at most ${max} characters.`,
      },
      description: {
        label: DESCRIPTION,
        placeholder: "What this dashboard is for",
      },
      icon: {
        label: "Icon",
        defaultOption: "Default",
      },
    },
    /** Appearance → Theme colours (`DashboardThemeSection`, ADR-0380). */
    theme: {
      title: "Theme colours",
      caption:
        "Used by lines, borders, shapes and table colour rules on this dashboard, and by charts set to theme colours. Other charts use the account's chart colours.",
      followsAccount: "These follow the account's chart colours until you change one.",
      slotLabel: (n: number) => `Theme colour ${n}`,
      usedBy: (n: number) => `Used by ${n} ${n === 1 ? "item" : "items"} on this dashboard`,
      invalid: "Enter a colour as #rrggbb, for example #1f6feb.",
      failed: "The colour was not saved. Please try again.",
      reset: "Use the account palette",
      resetFailed: "The account palette was not restored. Please try again.",
    },
    filterBarMoved: {
      text: "Filter bar settings moved to Filters.",
      action: "Open Filters",
    },
    publishing: {
      lockedMessage: "Only a workspace owner or an account admin can change this.",
      manual: {
        label: "Publish changes manually",
        on: "Viewers see the published version only. Your changes stay in a draft until you publish them.",
        off: "Viewers see your changes immediately.",
      },
      reviewRequired: (scopeLabel: string) => `Review is required in ${scopeLabel}.`,
      review: {
        label: "Require review before publishing",
        description: "A workspace owner or an account admin approves every change before viewers see it.",
      },
    },
  },

  /** The Filters pane's last section (`ViewerFilteringSection`): what
   *  viewers can filter themselves. Saved through the dashboard and topic
   *  PATCH, never the settings route. */
  viewerFiltering: {
    title: "Viewer filtering",
    intro:
      "Viewers filter a page themselves with controls, by clicking a value in a block, and from the filter bar when it is shown.",
    addControl: "To add a control, click Controls in the row above the dashboard.",
    lockedMessage: "You can't change this dashboard's settings.",
    draftNote: "Viewers see this change once you publish.",
    showFilterBar: {
      label: "Show filter bar",
      description:
        "Viewers can add their own filters from a bar above the blocks. Filters set by a click always show, even when the bar is hidden. A page can override this.",
    },
    confirmClickFilter: {
      label: "Confirm before filtering on a click",
      on: "A click on a value asks before it filters the page.",
      off: "A click on a value filters the page at once, with an option to undo.",
      interactionsPointer: "Which blocks a click filters is set in each block's Interactions section.",
    },
    page: {
      none: "Open a page to change its filter bar.",
      filterBar: (pageName: string) => `Filter bar on ${pageName}`,
      show: "Show",
      hide: "Hide",
      /** `state` is the dashboard's own setting: "shown" or "hidden". */
      filterBarHint: (state: string) =>
        `The default follows the dashboard's Show filter bar setting (now ${state}). Filters set by a click always show.`,
    },
    /** The closed section's row: the filter bar on the page on screen, and
     *  whether a click asks first. */
    summary: (barShown: boolean, confirm: boolean) =>
      `${barShown ? "Filter bar shown" : "Filter bar hidden"}${confirm ? " · clicks ask first" : ""}`,
  },

  /** The report editor's settings (plan 2026-10-10 settings catalogue, D1).
   *  A concept a dashboard has too takes the dashboard's group above
   *  (`compare`, `sortLimit`, `table`, `kpi.border`, ...); only what a
   *  report alone has, or says differently because it IS different, is
   *  here. */
  report: {
    /** The report's own settings: the Settings rail entry and the create
     *  drawer. Its default comparison is the same "Compare with" as the
     *  blocks' chips (`compare.label`, ADR-0267). */
    settings: {
      description: DESCRIPTION,
      compareDescription: "The default for every block; a block can choose its own comparisons, target included.",
      noComparison: "No comparison",
      periodDescription:
        "The window a block renders unless it picks its own. Changing it re-runs every block that uses the report's period.",
      /** The Settings pane's sections (plan 2026-10-13, Arjen's design). */
      sections: {
        general: "General",
        generalSummary: "Title & description",
        defaults: "Defaults",
        brand: "Brand",
        brandSummary: (custom: boolean) => `Account-wide · ${custom ? "Custom" : "Verity default"}`,
      },
      /** The pane header's subtitle per report pane. */
      paneLabel: { pages: "Pages", settings: "Settings", chat: "Chat" } as Record<string, string>,
    },
    create: {
      compareDescription: "What the deltas and the trend line are measured against.",
      targetUnavailable: (modeLabel: string) => `${modeLabel} (no targets yet)`,
    },
    /** A data block's Configure pane (`ReportBlockConfigurator`). */
    block: {
      visualization: "Visualization",
      /** A chart over time's grain: the dashboard's "Group by" (pivot). */
      groupBy: GROUP_BY,
      optionalDimensions: "Optional: split the series by a dimension.",
      changeSortNeedsComparison: "Sort by change needs a comparison with an earlier period.",
      /** A page report's scorecard strip draws a line under each KPI; a
       *  flowing report's KPI is a card, `kpi.border.defaultLabel`. */
      kpiBorderLineUnder: "Line under (default)",
    },
  },

  /** The edit panel's Filters pane (`ScopeFiltersSection`). */
  scopeFilters: {
    dashboard: {
      title: "This dashboard",
      description: "Applies to every block on every page.",
      empty: "No filters on this dashboard",
    },
    /** A dashboard's tab is called a page in every editor text (Decision 2). */
    tab: {
      title: (tabName: string) => `This page: ${tabName}`,
      description: "Applies to every block on this page, after the dashboard's filters.",
      empty: "No filters on this page",
    },
    coverage: {
      none: "Applies to no block",
      all: "Applies to every block",
      some: (covered: number, inScope: number) => `Applies to ${covered} of ${inScope} blocks`,
    },
    saveForReuse: "Save for reuse",
    nameAria: "Saved filter name",
    newNameAria: "New saved filter name",
    namePlaceholder: "Name this filter",
    saved: {
      title: "Saved filters",
      description: "Name a filter once, then apply it to the dashboard, a page or a block.",
      empty: "No saved filters yet",
      new: "New saved filter",
      needsCondition: "A saved filter needs at least one condition",
      conditions: (n: number) => count(n, "condition", "conditions"),
    },
  },

  /** A background shape's panel (`BackdropShapePanel`, plan 2026-10-11),
   *  dashboard and report alike. */
  backdrop: {
    subtitle: "Background shape",
    shape: "Shape",
    cornerRadius: "Corner radius",
    direction: "Direction",
    directionAria: "Arrow direction",
    style: "Style",
    styleAria: "Arrow style",
    text: "Text",
    textSize: "Text size",
    tail: "Tail",
    fill: { title: "Fill", label: "Fill", opacity: "Opacity" },
    outline: {
      title: "Outline",
      thickness: "Outline thickness",
      style: "Outline style",
      colour: "Outline colour",
      shadow: "Border shadow",
      shadowDescription: "A soft shadow along the outline.",
    },
    arrange: {
      title: "Arrange",
      keepProportions: "Keep proportions",
      keepProportionsDescription: "When cleared, the shape stretches to the box you draw.",
      bringForward: "Bring forward",
      sendBackward: "Send backward",
      delete: "Delete",
    },
    footnote:
      "Shapes sit behind your blocks. Drag to move, use the handles to resize, and hold Shift to snap to a guide. Press Esc to go back to your blocks.",
    px: (n: number) => `${n} px`,
    pct: (n: number) => `${n}%`,
  },

  /** A smart text block (plan 2026-10-16-a-smart-text-block, S5; ADR-0426):
   *  its panel, its first-metric dialog and the hints its tile shows. */
  smartText: {
    subtitle: "Smart text block",
    data: {
      title: "Data",
      note: "Every variable reads from this dataset.",
      lockedNote: "Every variable reads from this dataset. To use another one, first remove the variables that read a metric.",
    },
    variables: {
      title: "Variables",
      summary: (n: number) => count(n, "variable", "variables"),
      add: "Add variable",
      addToText: "Add to text",
      cancel: "Cancel",
      edit: (name: string) => `Edit ${name}`,
      remove: (name: string) => `Remove ${name}`,
      empty: "No variables yet. Add one to put a live number in the text.",
      tooMany: (max: number) => `A smart text block takes at most ${max} variables.`,
      name: "Name",
      nameDescription: "Shown in the text as a chip. Lower-case letters, digits and _.",
      nameInvalid: "Start with a lower-case letter and use only a-z, 0-9 and _ (at most 32 characters).",
      nameTaken: "Another variable already has this name.",
      kind: "Kind",
      kinds: { value: "Value", ratio: "Share", delta: "Change", period: "Period" },
      kindDescriptions: {
        value: "The metric over the period on screen.",
        ratio: "The metric as a percentage of another metric.",
        delta: "How the metric changed against an earlier period.",
        period: "The name or the dates of the period on screen.",
      },
      metric: "Metric",
      ofMetric: "As a share of",
      compare: "Compare with",
      compareModes: { previous_period: "Previous period", same_period_last_year: "Same period last year" },
      showAs: "Show as",
      showAsModes: { absolute: "Amount", percent: "Percentage" },
      periodStyle: "Show",
      periodStyles: { name: "Name", range: "Dates", name_and_range: "Name and dates" },
      periodCase: "Position in sentence",
      periodCases: { sentence: "Starts the sentence", lower: "Inside the sentence" },
    },
    language: {
      label: "Number language",
      description: "How numbers and period names are written. Your text stays as you typed it.",
      options: { en: "English", nl: "Dutch" },
    },
    issues: {
      heading: "Not saved yet:",
      unknownToken: (name: string) => `{{${name}}} is not a variable.`,
      unused: (name: string) => `${name} is not used in the text.`,
      noMetric: "Add a variable that reads a metric.",
      empty: "Type some text.",
      /** The server's own sentences (`smart_text.py::parse_template`). */
      malformedToken: "Text between {{ and }} must be a variable name (lower-case letters, digits and _, no spaces).",
      misplacedToken: "A variable cannot stand inside code or a link address; move it into the text.",
      /** On the tile, outside edit mode, while a draft could not be saved. */
      unsaved: "Unsaved changes:",
      tooLong: (max: number) => `This block is limited to ${max} characters.`,
    },
    filters: {
      metricsNotAllowed: "A smart text block filters by dimension only.",
    },
    newBlock: {
      title: "New smart text",
      description: "Choose the first number your text shows. Every number in this block comes from the same dataset.",
      create: "Create",
      cancel: "Cancel",
    },
  },
  /** The line under a chart or table block's title (plan 2026-10-16-a-block-
   *  subtitle, step 4). Variables reuse `smartText.variables`' words. */
  subtitle: {
    label: "Subtitle",
    placeholder: "Add a subtitle (optional)",
    insertVariable: "Insert variable",
    tooMany: (max: number) => `A subtitle takes at most ${max} variables.`,
    tooLong: (max: number) => `A subtitle is limited to ${max} characters.`,
  },
  /** The block's card background (plan 2026-10-17 the Format tab on every
   *  data block): the first section of the Format tab. */
  cardBackground: {
    title: "Card background",
    /** Before the server's reason, when there is one; alone otherwise. */
    failed: "The card background was not saved.",
    tryAgain: "Please try again.",
  },
  /** A tile's frame (plan 2026-10-16-tile-frame-style-settings): the block
   *  editor, the dashboard's Appearance and the account's Dashboard tiles. */
  tileFrame: {
    title: "Tile frame",
    caption: "How every block on this dashboard is framed. A block can override it.",
    titleRule: "Line under the title",
    corners: "Corners",
    outline: "Border",
    on: "On",
    off: "Off",
    rounded: "Rounded",
    square: "Square",
    /** The subtitle's width (plan 2026-10-16-a-subtitle-may-keep-a-reading-width). */
    subtitleWidth: "Subtitle width",
    subtitleFull: "Full",
    subtitleNarrow: "Narrow",
    subtitleWidthHint: "Narrow keeps the subtitle to a reading width and shows up to four lines.",
    /** The level a field's default comes from, per level. */
    dashboardDefault: "Dashboard",
    accountDefault: "Account",
    verityDefault: "Default",
    /** Arjen, 2026-10-16: a field shows the value in effect (the default
     *  until changed) and, once changed, says it overrides the default.
     *  Picking the default's value again goes back to following it. */
    overrides: (from: string, value: string) =>
      from === "Default"
        ? `Overrides Verity's default (${value.toLowerCase()}).`
        : `Overrides the ${from.toLowerCase()} default (${value.toLowerCase()}).`,
    transparentNote: "This block has no card background, so it draws no border or line.",
    failed: "The frame was not saved. Please try again.",
  },
  /** Fonts per text role (plan 2026-10-16-dashboard-font-choices), at the
   *  block, dashboard and account level. */
  fonts: {
    title: "Fonts",
    caption: "The fonts of this dashboard's text. A block can override them.",
    accountCaption: "The fonts of every dashboard in your organization, managed dashboards included. A dashboard or block can override them.",
    blockCaption: "This block's own text styles. Pick the (default) value to follow the dashboard.",
    roles: {
      title: "Headings",
      text: "Text",
      chart_title: "Block titles",
      chart_subtitle: "Subtitles",
      table_header: "Table headers",
      table_body: "Table content",
      kpi_value: "Main metric",
      control_label: "Control labels",
      control_value: "Control values",
    },
    serif: "Serif",
    sans: "Sans serif",
    /** The level names, for the "Overrides the … default" line. */
    dashboardDefault: "Dashboard",
    accountDefault: "Account",
    reset: "Reset to Verity default",
    failed: "The font was not saved. Please try again.",
    /** Text colours per role (plan 2026-10-17-dashboard-font-colours), in
     *  the same rows as the fonts. */
    colorLabel: (role: string) => `${role} colour`,
    colorTokens: { text: "Text", muted: "Muted" } as Record<string, string>,
    hardToRead: (ratio: number) => `Hard to read on the dashboard background (${ratio.toFixed(1)}:1).`,
    colorLightOnly: "Theme and custom colours show in light mode. Text and Muted also adapt to dark mode.",
    colorFailed: "The colour was not saved. Please try again.",
    /** Text sizes per role (plan 2026-10-17-dashboard-font-sizes). */
    sizeLabel: (role: string) => `${role} size`,
    sizeHint: "A larger size may need a larger block; a main metric still shrinks to fit a narrow one.",
    sizeFailed: "The size was not saved. Please try again.",
    /** The heading/text block panel (plan 2026-10-17-dashboard-font-sizes-and-text-block-panel). */
    textBlockSubtitle: "Edit the text on the block itself.",
    /** The platform's body font is not a catalogue font (plan 2026-10-17-
     *  dashboard-text-style-complete, D2): its option names what draws. */
    systemFont: "System font",
    /** The rest of the text style per role (D5, D8). */
    weightLabel: (role: string) => `${role} weight`,
    weights: { 400: "Regular", 500: "Medium", 600: "Semibold", 700: "Bold" } as Record<number, string>,
    italic: "Italic",
    underline: "Underline",
    strikethrough: "Strikethrough",
    uppercase: "Uppercase",
    alignLabel: (role: string) => `${role} alignment`,
    aligns: { left: "Left", center: "Centre", right: "Right" } as Record<string, string>,
    lineSpacing: "Line spacing",
    lineSpacings: { compact: "Compact", normal: "Normal", relaxed: "Relaxed" } as Record<string, string>,
    /** A toggle's accessible name: role, attribute and state. */
    toggleName: (role: string, attribute: string, on: boolean, isDefault: boolean) =>
      `${role} ${attribute.toLowerCase()}, ${on ? "on" : "off"}${isDefault ? " (default)" : ""}`,
    styleFailed: "The text style was not saved. Please try again.",
  },
  /** The look of control blocks (plan 2026-10-19-control-block-styling, D6):
   *  a control's Format pane and the dashboard's Appearance → Controls. */
  controlStyle: {
    title: "Controls",
    caption: "How the controls on this dashboard look. A control can override it.",
    blockCaption: "This control's own look. Pick the (default) value to follow the dashboard.",
    sections: { values: "Values", text: "Text", tile: "Tile" },
    selected: "Selected",
    notSelected: "Not selected",
    background: "Background",
    border: "Border",
    text: "Text",
    /** A colour field's accessible name: "Selected background". */
    colorLabel: (state: string, attribute: string) => `${state} ${attribute.toLowerCase()}`,
    borderWidth: "Border width",
    corners: "Corners",
    size: "Size",
    spacing: "Spacing",
    sizes: { xs: "Small", sm: "Medium", md: "Large" } as Record<string, string>,
    px: (n: number) => `${n} px`,
    pill: "Pill",
    /** What a field reads while nothing above sets it: each control keeps
     *  its own look. */
    automatic: "Automatic",
    tokens: { rule: "Line", muted: "Muted", text: "Text", primary: "Brand", primary_light: "Brand light" } as Record<string, string>,
    checkboxTextNote: "The option text uses the Not selected text colour.",
    hardToRead: (state: string, ratio: number) =>
      `${state} text is hard to read on its background (${ratio.toFixed(1)}:1).`,
    colorLightOnly: "Theme and custom colours stay the same in dark mode.",
    card: "Card",
    noCard: "No card",
    tileBorder: "Border",
    tileBorderDefault: "Tile (default)",
    noCardNote: "This control has no card, so it draws no border.",
    failed: "The control style was not saved.",
  },
  /** An image block's panel (plan 2026-10-23-an-image-block-on-a-dashboard,
   *  D6): the Image section of its Format tab. */
  image: {
    title: "Image",
    subtitle: "Image block",
    replace: "Replace image",
    fit: "Fit",
    contain: "Contain",
    cover: "Cover",
    fitHint: "Contain shows the whole image. Cover fills the block and may crop the edges.",
    align: "Alignment",
    left: "Left",
    center: "Center",
    right: "Right",
    alt: "Alt text",
    altHint: "Describes the image for screen readers. Leave it empty for a decorative image.",
    altOneLine: "Alt text is one line.",
    altTooLong: (max: number) => `Alt text is limited to ${max} characters.`,
    failed: "The image was not saved.",
  },
  /** The account's dashboard colours (plan 2026-10-16-account-dashboard-
   *  appearance, D8), on the account preferences page. */
  dashboardSurface: {
    title: "Dashboard colours",
    caption:
      "The page and block colours of every dashboard in your organization, managed dashboards included.",
    lightOnly: "Colours apply in light mode. The dark theme keeps its own colours.",
    presets: "Colour preset",
    custom: "Custom",
    pageBg: "Page background",
    blockBg: "Block background",
    reset: "Reset to default",
    resetPending: "Save to go back to Verity's default colours.",
    hexInvalid: "Enter a hex colour such as #f5f3ee.",
    failed: "The dashboard colours were not saved.",
  },
} as const;
